/**
 * @prettier
 */
import isempty from 'lodash/isEmpty';

// Relative Imports
import {pricing} from '../api/pricing';
import {pricingGraphql} from '../api/pricingGraphQl';
import {log as logToServer} from '../api/logger';
import edapActions from '../actions/edapActions';
import listingActions from '../actions/listingActions';
import {priceErrorMap, types} from './errorEvents';
import {Map} from 'immutable';
import {evaluateExperiment, isInBucketData} from './abacusHelper';

function createDefaultPricingKey(tripData, systemData) {
    return new Map({
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        adults: tripData.adults,
        children: tripData.children,
        pets: tripData.pets,
        currency: systemData.currency
    });
}

// comparePricing is a temp function only to validate calling graphql.
// It will be removed shortly.
/* istanbul ignore next */
/* eslint-disable complexity */
function comparePricing(legacyData, graphQlPricingData, graphQlTimeDiff, displayLocale) {
    const quote = legacyData.pdpQuote || {};
    const legacyCheckoutError = legacyData.checkoutError || {};
    // Check difference of key data, and see what we see!
    let graphQlErrorCode = '';
    let legacyErrorCode = legacyCheckoutError.errorCode || '';
    // errorCode
    if (graphQlPricingData.statusCode && graphQlPricingData.originalErrorCode) {
        graphQlErrorCode = graphQlPricingData.originalErrorCode;
    }

    if (graphQlErrorCode || legacyErrorCode) {
        // If there were any errors, we don't want to compare prices.
        if (!!graphQlErrorCode.length && !!legacyErrorCode.length) {
            // however, only compare errors if both errored out.
            logToServer({
                pricingGraphQlSame: graphQlErrorCode === legacyErrorCode,
                graphqlPricingError: true,
                errorSame: graphQlErrorCode === legacyErrorCode,
                graphQlErrorCode,
                legacyErrorCode,
                graphQlTimeDiff,
                displayLocale
            });
        }
    } else {
        let legacyAverageNightly = '';
        let graphQlNightly = '';
        let nightlySame = false;

        // perNight
        if (quote.averageNightly) {
            legacyAverageNightly = (
                quote.averageNightly.find((currentTotal) => {
                    return currentTotal.type === 'TOTAL_RENT';
                }) || {perNightCost: {localized: 'NOTFOUND'}}
            ).perNightCost.localized;
        }
        if (graphQlPricingData.priceDetails && graphQlPricingData.priceDetails.averageNightlies) {
            graphQlNightly = (
                graphQlPricingData.priceDetails.averageNightlies.find((currentTotal) => {
                    return currentTotal.type === 'TOTAL_RENT';
                }) || {perNightCost: {localized: 'NOTFOUND'}}
            ).perNightCost.localized;
        } else {
            graphQlNightly = '';
        }

        nightlySame = legacyAverageNightly.replace(/\s/g, '') === graphQlNightly.replace(/\s/g, '');
        // total
        let legacyTotal = '';
        let graphQlTotal = '';
        // This big blob of code comees from selectors/priceDetails::getTotals()
        if (quote.quoteTotals) {
            const totalWithStayCollectedFeesWithoutDepositsItem = quote.quoteTotals.additionalTotals.find(
                (additionalTotal) => {
                    return additionalTotal.name === 'totalWithStayCollectedFeesWithoutDeposits';
                }
            );
            const totalWithoutDepositsItem = quote.quoteTotals.additionalTotals.find(
                (additionalTotal) => {
                    return additionalTotal.name === 'totalWithoutDeposits';
                }
            );
            const totalWithoutDeposits =
                totalWithStayCollectedFeesWithoutDepositsItem || totalWithoutDepositsItem;

            legacyTotal = totalWithoutDeposits && totalWithoutDeposits.total.localized;
        }
        // This is what will replace that big blob of data when we move to the partials.
        if (
            graphQlPricingData &&
            graphQlPricingData.priceDetails &&
            graphQlPricingData.priceDetails.totals &&
            graphQlPricingData.priceDetails.totals.length
        ) {
            graphQlTotal = graphQlPricingData.priceDetails.totals[0].amount;
        }
        const totalSame = legacyTotal.replace(/\s/g, '') === graphQlTotal.replace(/\s/g, '');

        const graphqlCheckoutUrl =
            (graphQlPricingData.priceDetails && graphQlPricingData.priceDetails.checkoutUrl) || '';
        const legacyCheckoutUrl = legacyData.checkoutUrl || '';
        const checkoutUrlSame =
            graphqlCheckoutUrl.length || legacyCheckoutUrl.length
                ? graphqlCheckoutUrl === legacyCheckoutUrl
                : true;

        logToServer({
            pricingGraphQlSame: nightlySame && totalSame && checkoutUrlSame,
            graphqlPricingError: false,
            nightlySame,
            legacyAverageNightly,
            graphQlNightly,
            totalSame,
            legacyTotal,
            graphQlTotal,
            checkoutUrlSame,
            graphqlCheckoutUrl: graphqlCheckoutUrl || '',
            legacyCheckoutUrl,
            graphQlTimeDiff,
            displayLocale
        });
    }
}
/* eslint-enable complexity */

function getError(checkoutError) {
    const errorCode = checkoutError ? checkoutError.errorCode : 'UNDEFINED';

    if (errorCode) {
        return priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;
    }

    return null;
}

/* eslint-disable complexity */
function handleError(store, pageLoadPriceCall = false, checkoutError, quoteEventParams) {
    // eslint-disable-line complexity
    const error = getError(checkoutError);

    if (error) {
        const validDates = error.type !== types.DATES;
        const validGuests = error.type !== types.GUESTS;

        store.dispatch(
            listingActions.datesAreValid({
                datesValid: validDates,
                hasQuoteError: !validDates
            })
        );

        store.dispatch(listingActions.guestsAreValid(validGuests));

        const {listingReducer, tripDetailsReducer, systemReducer} = store.getState();

        if (!validDates || !validGuests) {
            const defaultPricingKey = createDefaultPricingKey(tripDetailsReducer, systemReducer);
            store.dispatch(listingActions.pricingInfoReset(defaultPricingKey));
        }

        store.dispatch(edapActions.triggerEdapEvent('quote.failure', quoteEventParams));

        const graphqlRequestMarker =
            checkoutError && checkoutError.graphqlRequestMarker
                ? checkoutError.graphqlRequestMarker
                : null;

        logToServer({
            message: 'quote.attempt',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function handleGraphQlResult(
    store,
    pageLoadPriceCall,
    priceDetails,
    arrival,
    departure,
    adults,
    children,
    currency,
    pets,
    clientPriceFetchTime,
    displayLocale,
    defaultadults
) {
    let graphQlErrorCode = '';
    const {abacusTestsReducer, listingReducer} = store.getState();
    const isFreeCancellationTestOn = isInBucketData(
        abacusTestsReducer,
        'HA_PDP_RevampFreeCancellationMessaging',
        1
    );

    // We will fallback to statusCode.  I have seen a known bug where originalErrorCode is not
    // provided.
    if (priceDetails.statusCode || priceDetails.originalErrorCode) {
        graphQlErrorCode = priceDetails.originalErrorCode || priceDetails.statusCode;
    }

    const priceDetailsData = priceDetails.priceDetails || {};
    const quoteEventParams = priceDetailsData.edapQuoteSuccess
        ? JSON.parse(priceDetailsData.edapQuoteSuccess)
        : {};
    quoteEventParams.freecancellation = isFreeCancellationTestOn;
    quoteEventParams.defaultadults = defaultadults;
    if (graphQlErrorCode || isempty(priceDetailsData)) {
        const checkoutError = {
            errorCode: graphQlErrorCode || 'API_BROKEN'
        };

        if (priceDetails.graphqlRequestMarker) {
            checkoutError.graphqlRequestMarker = priceDetails.graphqlRequestMarker;
        }

        const quoteFailureEventParams = {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`
        };

        handleError(store, pageLoadPriceCall, checkoutError, quoteFailureEventParams);
    } else {
        store.dispatch(edapActions.triggerEdapEvent('quote.success', quoteEventParams));
        store.dispatch(edapActions.logTestExposure('HA_PDP_RRFirstPayment'));
        logToServer({
            message: 'quote.attempt',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function handlePDSResult(
    store,
    pageLoadPriceCall,
    pricingInfo,
    arrival,
    departure,
    adults,
    children,
    currency,
    pets,
    clientPriceFetchTime,
    defaultadults
) {
    const pdpQuote = pricingInfo.pdpQuote;
    let checkoutError = pricingInfo.checkoutError;
    // For availability logging
    const {listingReducer, abacusTestsReducer, travelerFeaturesReducer} = store.getState();
    const quoteEventParams = {
        eventstartdate: arrival,
        eventenddate: departure,
        eventnumadults: `${adults}`,
        eventnumchildren: `${children}`
    };

    if (!pdpQuote) {
        if (!checkoutError) {
            // Something bad has happened.  The API might have changed the name of the error attribute again.
            checkoutError = {
                id: 'UnsetErrorFromAPI',
                errorCode: 'API_BROKEN',
                message:
                    'The Pricing API has returned neither a pdpQuote nor a checkoutError. Please consult with the PDS team.'
            };
            // Assign this (mutate) the original object, since it gets dispatched, and stored.
            pricingInfo.checkoutError = checkoutError;

            // This should never appear in the logs.  If it does, then it is a P1
            logToServer({pricingApiBroken: true});
        }

        handleError(store, pageLoadPriceCall, checkoutError, quoteEventParams);
    } else {
        quoteEventParams.freecancellation = isInBucketData(
            abacusTestsReducer,
            'HA_PDP_RevampFreeCancellationMessaging',
            1
        );
        quoteEventParams.defaultadults = defaultadults;
        if (pdpQuote && pdpQuote.quoteTotals) {
            const totalWithoutDeposits = pdpQuote.quoteTotals.additionalTotals.find((total) => {
                return total.name === 'totalWithoutDeposits';
            });
            const quoteAmountInt = totalWithoutDeposits
                ? totalWithoutDeposits.total.amount
                : pdpQuote.quoteTotals.total.amount;

            quoteEventParams.eventquoteamount = quoteAmountInt.toFixed(2);
            quoteEventParams.eventquotecurrency = pdpQuote.quoteTotals.total.currency;

            // Write to edap
            window.analyticsdatalayer.displayquotetotal = quoteAmountInt;
        }

        if (pdpQuote && pdpQuote.items) {
            const rent = pdpQuote.items.find((item) => {
                return item.subType === 'RENT';
            });

            quoteEventParams.fullorderrentalrate =
                rent && rent.amount ? rent.amount.amount.toFixed(2) : '0.00';

            const customFees = pdpQuote.items.filter((item) => {
                return item.type === 'FEE' && !item.subType;
            });

            quoteEventParams.numpartnerfees = customFees.length;

            const tax = pdpQuote.items.filter((item) => {
                return item.type === 'TAX';
            })[0];

            quoteEventParams.ordertax = tax && tax.amount ? tax.amount.amount.toFixed(2) : '0.00';

            let customFeesTotal = 0;

            for (let fee of customFees) {
                customFeesTotal += fee.amount.amount;
            }

            quoteEventParams.partnerfeeamount = customFeesTotal.toFixed(2);

            const serviceFee = pdpQuote.items.find((item) => {
                return item.subType === 'SERVICE_FEE';
            });

            quoteEventParams.servicefeeamount =
                serviceFee && serviceFee.amount ? serviceFee.amount.amount.toFixed(2) : '0.00';
        }

        // pick the average nightly price displayed on the page and log it as eventaveragenightly
        // this must be in line with listingSummary.js
        if (pdpQuote && pdpQuote.averageNightly) {
            const averageNightly = pdpQuote.averageNightly.find((rate) => {
                let rateType = 'TOTAL_RENT';
                if (travelerFeaturesReducer.includeTaxesAndFees) {
                    rateType = 'TOTAL_TRIP';
                }
                return rate.type === rateType;
            });
            if (averageNightly && averageNightly.perNightCost) {
                quoteEventParams.eventaveragenightly = averageNightly.perNightCost.localized;
            }
        }

        store.dispatch(edapActions.triggerEdapEvent('quote.success', quoteEventParams));
        store.dispatch(edapActions.logTestExposure('HA_PDP_RRFirstPayment'));
        logToServer({
            message: 'quote.attempt',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager
        });
    }
}
/* eslint-enable complexity */

async function whoCaresIfFailedPromise(name, fn) {
    return new Promise(async (resolve /* , reject */) => {
        try {
            const result = await fn;
            resolve(result);
        } catch (err) {
            // We will will hide this, and not allow it to kill any processing,
            // we do want to at least log that it happened.
            logToServer({
                comparingPricingServiceException: name,
                err: err.message
            });
            resolve({});
        }
    });
}

let cachedPdsPricingXHR = null;
async function pdsPricingCallWrapper({
    listingId,
    site,
    arrival,
    departure,
    adults,
    children,
    pets,
    isOnlineBookable,
    acceptsHomeAwayPayments,
    unitId,
    useMockData,
    unitApiUrl
}) {
    let result = await (cachedPdsPricingXHR = pricing({
        listingId,
        site,
        arrival,
        departure,
        adults,
        children,
        pets,
        isOnlineBookable,
        acceptsHomeAwayPayments,
        unitId,
        useMockData,
        unitApiUrl
    }));
    result.endTime = Date.now();
    return result;
}

/* eslint-disable complexity */
const pricingHelper = async ({
    store = {dispatch: () => {}},
    listingId = '',
    site = '',
    displayLocale = '',
    arrival = '',
    departure = '',
    adults = 0,
    children = 0,
    pets = 0,
    defaultadults = 'false',
    currency = '',
    loadingRate = () => {},
    loadedRate = () => {},
    isOnlineBookable = false,
    acceptsHomeAwayPayments = false,
    unitId = '',
    pageLoadPriceCall = false,
    useMockData = false,
    unitApiUrl = ''
}) => {
    store.dispatch(loadingRate(true));
    store.dispatch(
        edapActions.triggerEdapEvent('quote.attempt', {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );

    // abov=26895|x|x
    // variant 0 - Use pds pricing
    // variant 1 - Use graphql pricing
    const pricingConsistencySwitch = evaluateExperiment({
        name: 'ha_pdp_usePricingGraphQL',
        variant: 1,
        deferLogging: true
    });

    // ha_pdp_usePricingGraphQL (usePricingGraphQl) is our PDP only throttle.
    // abov=27450|x|x
    // When ha_pdp_comparePricingCalls is on, we want to compare against the other method of pricing
    // variant 0 - Do not compare
    // variant 1 - Compare pricing
    const comparePricingCalls = evaluateExperiment({
        name: 'ha_pdp_comparePricingCalls',
        variant: 1,
        deferLogging: true
    });

    /* istanbul ignore next */
    const usePdsPricing =
        !pricingConsistencySwitch || (pricingConsistencySwitch && comparePricingCalls);
    /* istanbul ignore next */
    const useGraphqlPricing =
        pricingConsistencySwitch || (!pricingConsistencySwitch && comparePricingCalls);

    // We will 'queue up' the pricing calls based on those abacus switches.
    // The pricingConsistencySwitch determines the Primary call.  The secondary
    // call is only called if we need to compare, and is also wrapped.
    const pricingCallQueue = [];
    // The main timer
    const startPricingCall = Date.now();
    if (cachedPdsPricingXHR && cachedPdsPricingXHR.abort) {
        cachedPdsPricingXHR.abort();
        cachedPdsPricingXHR = null;
    }
    let cachedPricingCall = null;
    if (usePdsPricing) {
        cachedPricingCall = pdsPricingCallWrapper({
            // also sets cachedPdsPricingXHR
            listingId,
            site,
            arrival,
            departure,
            adults,
            children,
            pets,
            isOnlineBookable,
            acceptsHomeAwayPayments,
            unitId,
            useMockData,
            unitApiUrl
        });

        // LISTING-3068 - Currently pricingConsistencySwitch is always false, so ignored.
        /* istanbul ignore next */
        const queuedPricingCall = pricingConsistencySwitch
            ? whoCaresIfFailedPromise.bind(null, 'pdsPricing')(cachedPricingCall)
            : cachedPricingCall;
        pricingCallQueue.push(queuedPricingCall);
    } else {
        pricingCallQueue.push(Promise.resolve({}));
    }

    let graphQlCall;
    if (useGraphqlPricing) {
        graphQlCall = pricingGraphql(
            {
                unit: unitApiUrl,
                arrivalDate: arrival,
                departureDate: departure,
                adults,
                children,
                pets,
                listingRef: listingId,
                currency
            },
            displayLocale
        );

        // LISTING-3068 - Currently pricingConsistencySwitch is always false, so ignored.
        const queuedGraphqlPricingCall = pricingConsistencySwitch
            ? graphQlCall
            : whoCaresIfFailedPromise.bind(null, 'graphqlPricing')(graphQlCall);
        pricingCallQueue.push(queuedGraphqlPricingCall);
    } else {
        pricingCallQueue.push(Promise.resolve({}));
    }

    try {
        // Await on the queue of pricing calls.
        // One of them is the Primary call, that we will handle the data, and catch exceptions.
        // The Secondary call will resolve to an empty object, or data if we are comparing data.
        const [pdsPricingResult, graphqlPricingResult] = await Promise.all(pricingCallQueue);

        let pdsCallTime = 0;
        let graphqlCallTime = 0;
        let clientPriceFetchTime = 0;
        let pricingInfo = {};
        let listingPricingInfo = {}; // This is passed in to the compare function.
        const pricingKey = {
            arrival,
            departure,
            adults,
            children,
            currency,
            pets
        };
        if (usePdsPricing) {
            // We are currently stripping out only the target property data from
            // the PDS call.  The graphql call is already in that context.
            pricingInfo = pdsPricingResult.body || {};
            listingPricingInfo = pricingInfo[listingId] || {};
            pdsCallTime = pdsPricingResult.endTime - startPricingCall;
        }
        if (useGraphqlPricing) {
            graphqlCallTime = graphqlPricingResult.endTime - startPricingCall;

            if (graphqlPricingResult.errorCode && !graphqlPricingResult.originalErrorCode) {
                graphqlPricingResult.originalErrorCode = graphqlPricingResult.errorCode;
            }
        }

        if (pricingConsistencySwitch) {
            clientPriceFetchTime = graphqlCallTime;
            handleGraphQlResult(
                store,
                pageLoadPriceCall,
                graphqlPricingResult,
                arrival,
                departure,
                adults,
                children,
                currency,
                pets,
                clientPriceFetchTime,
                displayLocale,
                defaultadults
            );
        } else {
            clientPriceFetchTime = pdsCallTime;
            handlePDSResult(
                store,
                pageLoadPriceCall,
                listingPricingInfo,
                arrival,
                departure,
                adults,
                children,
                currency,
                pets,
                clientPriceFetchTime,
                defaultadults
            );
        }

        store.dispatch(loadedRate(pricingInfo, graphqlPricingResult, pricingKey));

        if (comparePricingCalls) {
            // If both were used (have a time greater than 0), then determine
            // the difference for logging sake.
            let graphqlCallTimeDifference =
                graphqlCallTime && pdsCallTime ? graphqlCallTime - pdsCallTime : 0;
            comparePricing(
                listingPricingInfo,
                graphqlPricingResult,
                graphqlCallTimeDifference,
                displayLocale
            );
        }
    } catch (err) {
        // The only exception that should be caught here is for the primary pricing call.
        // The secondary compared call was wrapped in the 'whoCaresIfFailedPromise' Promise.
        const checkoutError = {
            id: 'UNKNOWN',
            errorCode: 'INTERNAL_ERROR',
            message: `Pricing call error: ${err}`
        };

        handleError(store, pageLoadPriceCall, checkoutError, {});

        // If graphql was primary, fill in the empty structure's error code.
        store.dispatch(loadedRate({[listingId]: {checkoutError}}, {}, checkoutError));
        store.dispatch(loadingRate(false));
    } finally {
        cachedPdsPricingXHR = null;
    }

    return cachedPricingCall;
};
/* eslint-enable complexity */

export default pricingHelper;
export {
    getError,
    handleError,
    handlePDSResult,
    handleGraphQlResult,
    pdsPricingCallWrapper,
    pricingHelper,
    createDefaultPricingKey
};
